/**
 * Campaign View Component
 *
 * This component provides a comprehensive form for creating and editing campaigns.
 * It includes all campaign configuration sections:
 *
 * 1. Basic Information - name, type, state, priority, etc.
 * 2. General Configuration - conversation settings, file handling
 * 3. Translation Configuration - multi-language support
 * 4. Conversation Configuration - automatic messaging settings
 * 5. Time Settings Configuration - operating hours and days
 * 6. Agent Configuration - agent management settings
 * 7. Default Messages Configuration - pre-defined messages to send agents messages
 * 8. Default Questions & Answers Configuration - pre-defined Q&A pairs defined by the user
 *
 * Hidden Fields (auto-populated, not visible to user):
 * - workspace_id: Automatically populated from current workspace
 * - platform_id: Automatically resolved from current workspace
 *
 * Features:
 * - Form validation with visual feedback
 * - Reactive form controls with conditional validation
 * - Accessibility support (ARIA labels, screen readers)
 * - Loading states and error handling
 * - Auto-save workspace context
 */

import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule, NgClass } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators, FormArray, FormControl } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextarea } from 'primeng/inputtextarea';
import { DropdownModule } from 'primeng/dropdown';
import { CalendarModule } from 'primeng/calendar';
import { InputNumberModule } from 'primeng/inputnumber';
import { ActivatedRoute, Router } from '@angular/router';
import { CampaignService } from '../../../services/campaign.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { Campaign, CampaignState, CampaignType, LANGUAGE_OPTIONS } from '../../../models/campaign';
import { finalize, Subject, takeUntil } from 'rxjs';
import { CheckboxModule } from 'primeng/checkbox';
import { InputSwitchModule } from 'primeng/inputswitch';
import { MultiSelectModule } from 'primeng/multiselect';
import { InputGroupModule } from 'primeng/inputgroup';
import { TranslateModule, TranslateService } from '@ngx-translate/core';


@Component({
  selector: 'app-campaign-view',
  imports: [
    CommonModule,
    NgClass,
    FormsModule,
    ReactiveFormsModule,
    InputTextModule,
    InputTextarea,
    DropdownModule,
    CalendarModule,
    InputNumberModule,
    ToastModule,
    ButtonModule,
    TableModule,
    ConfirmDialogModule,
    CheckboxModule,
    InputSwitchModule,
    MultiSelectModule,
    InputGroupModule,
    TranslateModule,
  ],
  providers: [
    MessageService,
    ConfirmationService,
  ],
  templateUrl: './campaign-view.component.html',
  styleUrl: './campaign-view.component.scss',
  standalone: true
})
export class CampaignViewComponent implements OnInit, OnDestroy {
  campaignForm!: FormGroup;
  campaign: Campaign | null = null;

  isEditing: boolean = false;
  campaignId: string | null = null;
  isLoading: boolean = false;
  isSubmitting: boolean = false;
  newReason: string = '';
  userRole: string = '';
  private destroy$ = new Subject<void>();

  // New properties for managing default messages and Q&A creation
  newMessage: string = '';
  newQuestion: string = '';
  newAnswer: string = '';
  editingMessageIndex: number = -1;
  editingQAIndex: number = -1;

  // Add these properties to the class
  messageEditBuffer: string[] = [];
  qaEditBuffer: { question: string, answer: string }[] = [];

  // --- Automatic Notifications Queue Add/Edit ---
  newNotificationMessage: string = '';
  newNotificationTime: number | null = null;

  editingNotificationIndex: number = -1;
  notificationEditBuffer: { message: string; time: number }[] = [];

  private workspaceId: string | null = null;
  private platformId: number | null = null;
  public previousOrderPriorityAgent: any[] = []; // Store previous order_priority_agent data

  // Computed property for readonly mode
  get isReadonly(): boolean {
    return this.campaignId !== null && !this.isEditing;
  }

  // Computed property for showing edit buttons
  get canEdit(): boolean {
    return !this.isReadonly;
  }

  // Computed property for showing input forms (create mode or edit mode)
  get canShowInputs(): boolean {
    return !this.campaignId || this.canEdit;
  }

  // Computed property for create mode
  get isCreateMode(): boolean {
    return !this.campaignId;
  }

  get typeOptions() {
    return [
      { label: this.translate.instant('supervisor.campaignView.typeOptions.outbound'), value: 'Outbound' as CampaignType },
      { label: this.translate.instant('supervisor.campaignView.typeOptions.inbound'), value: 'Inbound' as CampaignType }
    ];
  }

  get stateOptions() {
    return [
      { label: this.translate.instant('supervisor.campaignView.stateOptions.active'), value: 'Activo' as CampaignState },
      { label: this.translate.instant('supervisor.campaignView.stateOptions.inactive'), value: 'Inactivo' as CampaignState },
      { label: this.translate.instant('supervisor.campaignView.stateOptions.paused'), value: 'Pausado' as CampaignState }
    ];
  }

  languageOptions = LANGUAGE_OPTIONS;

  get dayOptions() {
    return [
      { label: this.translate.instant('supervisor.campaignView.dayOptions.monday'), value: 'monday' },
      { label: this.translate.instant('supervisor.campaignView.dayOptions.tuesday'), value: 'tuesday' },
      { label: this.translate.instant('supervisor.campaignView.dayOptions.wednesday'), value: 'wednesday' },
      { label: this.translate.instant('supervisor.campaignView.dayOptions.thursday'), value: 'thursday' },
      { label: this.translate.instant('supervisor.campaignView.dayOptions.friday'), value: 'friday' },
      { label: this.translate.instant('supervisor.campaignView.dayOptions.saturday'), value: 'saturday' },
      { label: this.translate.instant('supervisor.campaignView.dayOptions.sunday'), value: 'sunday' }
    ];
  }

  hourOptions = Array.from({ length: 24 }, (_, i) => ({
    label: i.toString().padStart(2, '0'),
    value: i
  }));

  minuteSecondOptions = Array.from({ length: 60 }, (_, i) => ({
    label: i.toString().padStart(2, '0'),
    value: i
  }));

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private campaignService: CampaignService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private translate: TranslateService,
  ) { }

  ngOnInit(): void {
    this.initForm();
    this.getRole();
    this.loadWorkspaceData();
    this.getRouteParams();
  }

  /**
   * Load workspace data - this value is automatically populated
   * in the form but remains hidden from the user interface.
   * For new campaigns: uses current workspace data
   * For existing campaigns: preserves original workspace ID (readonly)
   */
  private loadWorkspaceData(): void {
    try {
      // const workspaceStr = sessionStorage.getItem('workspace');
      // if (!workspaceStr) {
      //   throw new Error('No se encontró información de workspace en sessionStorage');
      // }
      // const workspace = JSON.parse(workspaceStr);

      // if (!workspace?.id || !workspace?.companyPlatformId) {
      //   throw new Error('Workspace o PlatformId inválidos');
      // }

      const raw = sessionStorage.getItem('workspace')
                || sessionStorage.getItem('workpsaceId')
                || sessionStorage.getItem('workspaceId')
                || localStorage.getItem('workspace')
                || localStorage.getItem('workpsaceId')
                || localStorage.getItem('workspaceId');

      if (!raw) {
        throw new Error('No se encontró información de workspace en sessionStorage/localStorage');
      }

      const workspace = JSON.parse(raw);

      const id = workspace?.id || workspace?.workspaceId || workspace?.Id;
      const platformId = this.normalizePlatformId(
        workspace?.platformNumericId
        ?? workspace?.platformId
        ?? workspace?.platform_id
        ?? workspace?.PlatformId
      );

      if (!id) {
        throw new Error('Workspace o PlatformId inválidos en storage');
      }

      this.workspaceId = String(id);
      this.platformId = platformId;

      if (this.campaignForm && !this.campaignId) {
        this.campaignForm.patchValue({
          workspace_id: this.workspaceId,
        });
      }
    } catch (error) {
      console.error('Error loading workspace data:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('supervisor.campaignView.toast.contextError.summary'),
        detail: this.translate.instant('supervisor.campaignView.toast.contextError.detail'),
        life: 5000
      });
    }
  }

  initForm(): void {
    // Initialize form with complete campaign structure including hidden workspace field
    this.campaignForm = this.fb.group({
      // Basic Information Fields
      name: ['', [Validators.required, Validators.maxLength(100)]],
      campaign_name: ['', [Validators.required, Validators.maxLength(100)]],
      type: [null, Validators.required],
      state: [null, Validators.required],
      priority: [1, [Validators.required, Validators.min(1), Validators.max(100)]],
      ratio: ['', [Validators.required, Validators.pattern(/^[0-9]+:[0-9]+$/)]],
      current_action: ['', [Validators.required, Validators.maxLength(200)]],
      state_since: [new Date(), Validators.required],
      closing_reason: [[]],

      // Hidden field for workspace - auto-populated from current workspace
      workspace_id: ['10'],

      // General Configuration Section
      general_config: this.fb.group({
        accept_conversation: [true],
        enable_file_upload: [false],
        enable_file_download: [false],
        enable_ticket_field: [false]
      }),

      // Translation Configuration Section
      translation_config: this.fb.group({
        enable: [false],
        source_language: new FormControl({ value: null, disabled: true }, Validators.required),
        target_language: new FormControl({ value: null, disabled: true }, Validators.required)
      }),

      // Conversation Configuration Section
      conversation_config: this.fb.group({
        allow_automatic_message: [false],
        how_often_message: new FormControl({ value: 0, disabled: true }, [Validators.min(1)]),
        automatic_message_value: new FormControl({ value: '', disabled: true }, [Validators.maxLength(500)]),
        retries: new FormControl({ value: 0, disabled: true }, [Validators.min(0), Validators.max(10)]),
        automatic_notifications_queue: this.fb.array([])
      }),

      // Time Settings Configuration Section
      time_settings_config: this.fb.group({
        enable: [false],
        days: [[], Validators.required],
        start_time: this.fb.group({
          hours: [9, [Validators.required, Validators.min(0), Validators.max(23)]],
          minutes: [0, [Validators.required, Validators.min(0), Validators.max(59)]],
          seconds: [0, [Validators.required, Validators.min(0), Validators.max(59)]]
        }),
        end_time: this.fb.group({
          hours: [17, [Validators.required, Validators.min(0), Validators.max(23)]],
          minutes: [0, [Validators.required, Validators.min(0), Validators.max(59)]],
          seconds: [0, [Validators.required, Validators.min(0), Validators.max(59)]]
        })
      }),

      // Agent Configuration Section
      agent_config: this.fb.group({
        enable: [false],
        transfer_between_agent: new FormControl({ value: false, disabled: true }),
        enable_priority_agent: new FormControl({ value: false, disabled: true }),
        order_priority_agent: [[]]
      }),

      // Default Messages Configuration Section
      default_messages_config: this.fb.group({
        enable: [false],
        messages: [[]]
      }),

      // Default Questions & Answers Configuration Section
      default_questions_answers_config: this.fb.group({
        enable: [false],
        questions_answers: [[]]
      })
    });

    // Setup reactive behaviors for form sections
    this.setupFormReactiveBehaviors();
  }

  /**
   * Setup reactive behaviors for all form configuration sections
   * This method handles the conditional enabling/disabling of form controls
   * and applies validation rules based on parent configuration states
   */
  private setupFormReactiveBehaviors(): void {
    // Translation Configuration Reactive Behavior
    this.campaignForm.get('translation_config.enable')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(enabled => {
        const sourceControl = this.campaignForm.get('translation_config.source_language');
        const targetControl = this.campaignForm.get('translation_config.target_language');

        if (enabled) {
          sourceControl?.enable();
          targetControl?.enable();
          sourceControl?.setValidators(Validators.required);
          targetControl?.setValidators(Validators.required);
        } else {
          sourceControl?.disable();
          targetControl?.disable();
          sourceControl?.clearValidators();
          targetControl?.clearValidators();
          sourceControl?.setValue(null);
          targetControl?.setValue(null);
        }
        sourceControl?.updateValueAndValidity();
        targetControl?.updateValueAndValidity();
      });

    // Conversation Configuration Reactive Behavior
    this.campaignForm.get('conversation_config.allow_automatic_message')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(enabled => {
        const howOftenControl = this.campaignForm.get('conversation_config.how_often_message');
        const messageControl = this.campaignForm.get('conversation_config.automatic_message_value');
        const retriesControl = this.campaignForm.get('conversation_config.retries');

        if (enabled) {
          howOftenControl?.enable();
          messageControl?.enable();
          retriesControl?.enable();
          howOftenControl?.setValidators([Validators.required, Validators.min(1)]);
          messageControl?.setValidators([Validators.required, Validators.maxLength(500)]);
          retriesControl?.setValidators([Validators.min(0), Validators.max(10)]);
        } else {
          howOftenControl?.disable();
          messageControl?.disable();
          retriesControl?.disable();
          howOftenControl?.clearValidators();
          messageControl?.clearValidators();
          retriesControl?.clearValidators();
          howOftenControl?.setValue(0);
          messageControl?.setValue('');
          retriesControl?.setValue(0);
        }
        howOftenControl?.updateValueAndValidity();
        messageControl?.updateValueAndValidity();
        retriesControl?.updateValueAndValidity();
      });

    // Time Settings Configuration Reactive Behavior
    this.campaignForm.get('time_settings_config.enable')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(enabled => {
        const daysControl = this.campaignForm.get('time_settings_config.days');

        if (enabled) {
          daysControl?.setValidators([Validators.required, Validators.minLength(1)]);
        } else {
          daysControl?.clearValidators();
          daysControl?.setValue([]);
        }
        daysControl?.updateValueAndValidity();
      });

    // Agent Configuration Reactive Behavior
    this.campaignForm.get('agent_config.enable')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(enabled => {
        const transferControl = this.campaignForm.get('agent_config.transfer_between_agent');
        const priorityControl = this.campaignForm.get('agent_config.enable_priority_agent');

        if (enabled) {
          transferControl?.enable();
          priorityControl?.enable();
        } else {
          transferControl?.disable();
          priorityControl?.disable();
          transferControl?.setValue(false);
          priorityControl?.setValue(false);
        }
        transferControl?.updateValueAndValidity();
        priorityControl?.updateValueAndValidity();
      });

    // Default Messages Configuration Reactive Behavior
    this.campaignForm.get('default_messages_config.enable')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(enabled => {
        const messagesControl = this.campaignForm.get('default_messages_config.messages');

        if (enabled) {
          messagesControl?.setValidators([Validators.required, Validators.minLength(1)]);
        } else {
          messagesControl?.clearValidators();
          messagesControl?.setValue([]);
        }
        messagesControl?.updateValueAndValidity();
      });

    // Default Questions & Answers Configuration Reactive Behavior
    this.campaignForm.get('default_questions_answers_config.enable')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(enabled => {
        const qaControl = this.campaignForm.get('default_questions_answers_config.questions_answers');

        if (enabled) {
          qaControl?.setValidators([Validators.required, Validators.minLength(1)]);
        } else {
          qaControl?.clearValidators();
          qaControl?.setValue([]);
        }
        qaControl?.updateValueAndValidity();
      });
  }

  getRouteParams(): void {
    this.route.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        this.campaignId = params.get('id');
        if (this.campaignId) {
          this.loadCampaign(this.campaignId);
        }
      });

    this.route.queryParamMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(queryParams => {
        this.isEditing = queryParams.get('edit') === 'true';
        this.toggleFormState();
      });
  }

  loadCampaign(id: string): void {
    this.isLoading = true;
    this.campaignService.getCampaignById(id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isLoading = false)
      )
      .subscribe({
        next: (campaign: Campaign) => {
          if (campaign) {

            this.campaign = campaign;
            this.previousOrderPriorityAgent = campaign.agent_config?.order_priority_agent || [];
            this.workspaceId = campaign.workspace_id || this.workspaceId;
            this.platformId = this.normalizePlatformId(campaign.platform_id) ?? this.platformId;
            const stateSince = campaign.state_since instanceof Date
              ? campaign.state_since
              : new Date(campaign.state_since);
            this.campaignForm.patchValue({
              name: campaign.name,
              campaign_name: campaign.campaign_name,
              type: campaign.type,
              state: campaign.state,
              priority: campaign.priority,
              ratio: campaign.ratio,
              current_action: campaign.current_action,
              state_since: stateSince,
              closing_reason: campaign.closing_reason || [],
              workspace_id: campaign.workspace_id || this.workspaceId,
              general_config: campaign.general_config || {
                accept_conversation: true,
                enable_file_upload: false,
                enable_file_download: false,
                enable_ticket_field: false
              },
              translation_config: campaign.translation_config || {
                enable: false,
                source_language: null,
                target_language: null
              },
              conversation_config: {
                allow_automatic_message: campaign.conversation_config?.allow_automatic_message ?? false,
                how_often_message: campaign.conversation_config?.how_often_message ?? 0,
                automatic_message_value: campaign.conversation_config?.automatic_message_value ?? '',
                retries: campaign.conversation_config?.retries ?? 0,
                automatic_notifications_queue: [] // We'll patch the FormArray below
              },
              time_settings_config: campaign.time_settings_config || {
                enable: false,
                days: [],
                start_time: { hours: 9, minutes: 0, seconds: 0 },
                end_time: { hours: 17, minutes: 0, seconds: 0 }
              },
              agent_config: campaign.agent_config || {
                enable: false,
                transfer_between_agent: false,
                enable_priority_agent: false,
                order_priority_agent: []
              },
              default_messages_config: campaign.default_messages_config || {
                enable: false,
                messages: []
              },
              default_questions_answers_config: campaign.default_questions_answers_config || {
                enable: false,
                questions_answers: []
              }
            });
            // Patch the automatic_notifications_queue FormArray
            const queueArray = this.automaticNotificationsQueue;
            queueArray.clear();
            (campaign.conversation_config?.automatic_notifications_queue || []).forEach((notif, idx) => {
              queueArray.push(this.fb.group({
                id_retrie: [idx + 1],
                message: [notif.message],
                time: [notif.time]
              }));
            });
            this.toggleFormState();
          }
        },
        error: (error) => {
          this.handleError(this.translate.instant('supervisor.campaignView.toast.loadError'), error);
        }
      });
  }

  updateCampaign(): void {
    if (!this.campaignId || this.campaignForm.invalid) {
      this.markFormGroupTouched(this.campaignForm);
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('supervisor.campaignView.toast.incompleteForm.summary'),
        detail: this.translate.instant('supervisor.campaignView.toast.incompleteForm.detail'),
        life: 3000
      });
      return;
    }

    // Validate configuration sections
    if (!this.validateConfigurationSections()) {
      return;
    }

    const formData = this.prepareFormData();
    if (!formData.platform_id) {
      this.handleMissingPlatformId();
      return;
    }
    this.isSubmitting = true;

    this.campaignService.updateCampaignById(this.campaignId, formData)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isSubmitting = false)
      )
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('supervisor.campaignView.toast.updated.summary'),
            detail: this.translate.instant('supervisor.campaignView.toast.updated.detail'),
            life: 3000
          });
          this.navigateToCampaignList();
        },
        error: (error) => {
          this.handleError(this.translate.instant('supervisor.campaignView.toast.updateError'), error);
        }
      });
  }

  createCampaign(): void {
    if (this.campaignForm.invalid) {
      this.markFormGroupTouched(this.campaignForm);
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('supervisor.campaignView.toast.incompleteForm.summary'),
        detail: this.translate.instant('supervisor.campaignView.toast.incompleteForm.detail'),
        life: 3000
      });
      return;
    }

    // Validate configuration sections
    if (!this.validateConfigurationSections()) {
      return;
    }

    const formData = this.prepareFormData();
    if (!formData.platform_id) {
      this.handleMissingPlatformId();
      return;
    }
    console.log(formData, "DATA form campaign");
    this.isSubmitting = true;

    this.campaignService.createCampaign(formData)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isSubmitting = false)
      )
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('supervisor.campaignView.toast.created.summary'),
            detail: this.translate.instant('supervisor.campaignView.toast.created.detail'),
            life: 3000
          });
          this.navigateToCampaignList();
        },
        error: (error) => {
          this.handleError(this.translate.instant('supervisor.campaignView.toast.createError'), error);
        }
      });
  }

  testCreateCampaign(): void {
    if (this.campaignForm.invalid) {
      this.markFormGroupTouched(this.campaignForm);
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('supervisor.campaignView.toast.incompleteForm.summary'),
        detail: this.translate.instant('supervisor.campaignView.toast.incompleteForm.detail'),
        life: 3000
      });
      return;
    }

    if (!this.validateConfigurationSections()) {
      return;
    }

    const formData = this.prepareFormData();
    if (!formData.platform_id) {
      this.handleMissingPlatformId();
      return;
    }
    console.log(formData, "DATA form campaign (TEST)");
    this.messageService.add({
      severity: 'info',
      summary: this.translate.instant('supervisor.campaignView.toast.test.summary'),
      detail: this.translate.instant('supervisor.campaignView.toast.test.detail'),
      life: 3000
    });
  }

  // Prepare form data with explicit typing for campaign creation/update
  private prepareFormData(): Omit<Campaign, 'id'> {
    const formValues = this.campaignForm.getRawValue();
    const resolvedPlatformId = this.campaign?.platform_id ?? this.platformId;

    // Ensure state_since is properly formatted
    let stateSince = formValues.state_since;
    if (!(stateSince instanceof Date)) {
      stateSince = new Date(stateSince || new Date());
    }

    // Create new campaign data object with all fields including hidden workspace
    const campaignData: Omit<Campaign, 'id'> = {
      name: formValues.name || '',
      campaign_name: formValues.campaign_name || '',
      type: formValues.type as CampaignType,
      state: formValues.state as CampaignState,
      priority: Number(formValues.priority) || 1,
      ratio: formValues.ratio || '',
      current_action: formValues.current_action || '',
      state_since: stateSince,
      closing_reason: formValues.closing_reason || [],
      created_at: this.campaign?.created_at || new Date(),
      updated_at: new Date(),

      // Hidden field - for existing campaigns, preserve original value (readonly)
      workspace_id: this.campaign?.workspace_id || this.workspaceId || formValues.workspace_id,
      platform_id: resolvedPlatformId ?? undefined,

      general_config: {
        accept_conversation: formValues.general_config.accept_conversation || false,
        enable_file_upload: formValues.general_config.enable_file_upload || false,
        enable_file_download: formValues.general_config.enable_file_download || false,
        enable_ticket_field: formValues.general_config.enable_ticket_field || false
      },

      translation_config: {
        enable: formValues.translation_config.enable || false,
        source_language: formValues.translation_config.enable ?
          formValues.translation_config.source_language : null,
        target_language: formValues.translation_config.enable ?
          formValues.translation_config.target_language : null
      },

      conversation_config: {
        allow_automatic_message: formValues.conversation_config.allow_automatic_message || false,
        how_often_message: Number(formValues.conversation_config.how_often_message) || 0,
        automatic_message_value: formValues.conversation_config.automatic_message_value || '',
        retries: Number(formValues.conversation_config.retries) || 0,
        automatic_notifications_queue: formValues.conversation_config.automatic_notifications_queue || []
      },

      time_settings_config: {
        enable: formValues.time_settings_config.enable || false,
        days: formValues.time_settings_config.days || [],
        start_time: {
          hours: Number(formValues.time_settings_config.start_time.hours) || 9,
          minutes: Number(formValues.time_settings_config.start_time.minutes) || 0,
          seconds: Number(formValues.time_settings_config.start_time.seconds) || 0
        },
        end_time: {
          hours: Number(formValues.time_settings_config.end_time.hours) || 17,
          minutes: Number(formValues.time_settings_config.end_time.minutes) || 0,
          seconds: Number(formValues.time_settings_config.end_time.seconds) || 0
        }
      },

      agent_config: {
        enable: formValues.agent_config.enable || false,
        transfer_between_agent: formValues.agent_config.transfer_between_agent || false,
        enable_priority_agent: formValues.agent_config.enable_priority_agent || false,
        order_priority_agent: this.previousOrderPriorityAgent // Preserve existing data (hidden from user)
      },

      // New configuration sections
      default_messages_config: formValues.default_messages_config || {
        enable: false,
        messages: []
      },

      default_questions_answers_config: formValues.default_questions_answers_config || {
        enable: false,
        questions_answers: []
      }
    };

    return campaignData;
  }

  toggleFormState(): void {
    if (this.isEditing) {
      this.campaignForm.enable();
      // Re-apply conditional disabled states after enabling the form
      this.applyConditionalStates();
    } else {
      if (!this.campaignId) {
        this.campaignForm.enable();
        this.applyConditionalStates();
        return;
      }
      this.campaignForm.disable();
    }
  }

  /**
   * Apply conditional disabled states to form controls based on their parent settings
   * This method ensures all dependent fields maintain proper state when form is toggled
   */
  private applyConditionalStates(): void {
    // Translation config conditional states
    const translationEnabled = this.campaignForm.get('translation_config.enable')?.value;
    const sourceControl = this.campaignForm.get('translation_config.source_language');
    const targetControl = this.campaignForm.get('translation_config.target_language');

    if (translationEnabled) {
      sourceControl?.enable();
      targetControl?.enable();
    } else {
      sourceControl?.disable();
      targetControl?.disable();
    }

    // Conversation config conditional states
    const conversationEnabled = this.campaignForm.get('conversation_config.allow_automatic_message')?.value;
    const howOftenControl = this.campaignForm.get('conversation_config.how_often_message');
    const messageControl = this.campaignForm.get('conversation_config.automatic_message_value');
    const retriesControl = this.campaignForm.get('conversation_config.retries');

    if (conversationEnabled) {
      howOftenControl?.enable();
      messageControl?.enable();
      retriesControl?.enable();
    } else {
      howOftenControl?.disable();
      messageControl?.disable();
      retriesControl?.disable();
    }

    // Agent config conditional states
    const agentEnabled = this.campaignForm.get('agent_config.enable')?.value;
    const transferControl = this.campaignForm.get('agent_config.transfer_between_agent');
    const priorityControl = this.campaignForm.get('agent_config.enable_priority_agent');

    if (agentEnabled) {
      transferControl?.enable();
      priorityControl?.enable();
    } else {
      transferControl?.disable();
      priorityControl?.disable();
    }
  }

  getRole(): void {
    try {
      const userStr = localStorage.getItem('logged_in_agent');
      if (userStr) {
        const user = JSON.parse(userStr);
        this.userRole = user.role || '';
      }
    } catch (error) {
      console.error('Error parsing user data:', error);
      this.userRole = '';
    }
  }

  addReason(): void {
    if (this.newReason?.trim()) {
      const currentReasons = this.campaignForm.get('closing_reason')?.value || [];
      this.campaignForm.patchValue({
        closing_reason: [...currentReasons, this.newReason.trim()]
      });
      this.campaignForm.markAsDirty();
      this.newReason = '';
    }
  }

  removeReason(reasonToRemove: string): void {
    const currentReasons = this.campaignForm.get('closing_reason')?.value || [];
    this.campaignForm.patchValue({
      closing_reason: currentReasons.filter((reason: string) => reason !== reasonToRemove)
    });
    this.campaignForm.markAsDirty();
  }

  goBack(): void {
    this.navigateToCampaignList();
  }

  navigateToCampaignList(): void {
    this.router.navigate(['/supervisor'], { queryParams: { option: "Campañas" } });
  }

  // Helper methods for template
  isFieldDirty(fieldName: string): boolean {
    const field = this.campaignForm.get(fieldName);
    return field ? field.dirty : false;
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.campaignForm.get(fieldName);
    return field ? (field.invalid && field.touched) : false;
  }

  // Mark all fields as touched to trigger validation
  markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if ((control as any).controls) {
        this.markFormGroupTouched(control as FormGroup);
      }
    });
  }

  private handleError(message: string, error: any): void {
    console.error(message, error);
    this.messageService.add({
      severity: 'error',
      summary: this.translate.instant('supervisor.campaignView.toast.error'),
      detail: message,
      life: 5000
    });
  }

  private handleMissingPlatformId(): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error de contexto',
      detail: 'No se pudo resolver el platform_id de la campaña. Recargue el workspace e intente nuevamente.',
      life: 5000
    });
  }

  private normalizePlatformId(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
      return value;
    }

    const normalized = Number(String(value ?? '').trim());
    return Number.isFinite(normalized) && normalized > 0 ? normalized : null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Helper methods for managing dynamic form arrays and configurations

  /**
   * Add a new message to the default messages configuration
   */
  addDefaultMessage(): void {
    if (!this.newMessage.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('supervisor.campaignView.toast.emptyMessage.summary'),
        detail: this.translate.instant('supervisor.campaignView.toast.emptyMessage.detail'),
        life: 3000
      });
      return;
    }

    const messagesControl = this.campaignForm.get('default_messages_config.messages');
    const currentMessages = messagesControl?.value || [];

    messagesControl?.setValue([...currentMessages, this.newMessage.trim()]);
    this.campaignForm.markAsDirty();
    this.newMessage = '';

    this.messageService.add({
      severity: 'success',
      summary: this.translate.instant('supervisor.campaignView.toast.messageAdded.summary'),
      detail: this.translate.instant('supervisor.campaignView.toast.messageAdded.detail'),
      life: 2000
    });
  }

  /**
   * Remove a message from the default messages configuration
   */
  removeDefaultMessage(index: number): void {
    const messagesControl = this.campaignForm.get('default_messages_config.messages');
    const currentMessages = messagesControl?.value || [];

    if (index >= 0 && index < currentMessages.length) {
      currentMessages.splice(index, 1);
      messagesControl?.setValue([...currentMessages]);
      this.campaignForm.markAsDirty();

      this.messageService.add({
        severity: 'info',
        summary: this.translate.instant('supervisor.campaignView.toast.messageRemoved.summary'),
        detail: this.translate.instant('supervisor.campaignView.toast.messageRemoved.detail'),
        life: 2000
      });
    }
  }

  /**
   * Start editing a message at the specified index
   */
  editDefaultMessage(index: number): void {
    this.editingMessageIndex = index;
    const messages = this.campaignForm.get('default_messages_config.messages')?.value || [];
    this.messageEditBuffer[index] = messages[index];
  }

  /**
   * Update an existing message
   */
  updateDefaultMessage(): void {
    const messagesControl = this.campaignForm.get('default_messages_config.messages');
    const messages = messagesControl?.value || [];
    if (this.editingMessageIndex >= 0 && this.editingMessageIndex < messages.length) {
      messages[this.editingMessageIndex] = this.messageEditBuffer[this.editingMessageIndex] || '';
      messagesControl?.setValue([...messages]);
      this.campaignForm.markAsDirty();
      this.cancelEditMessage();
      this.messageService.add({
        severity: 'success',
        summary: this.translate.instant('supervisor.campaignView.toast.messageUpdated.summary'),
        detail: this.translate.instant('supervisor.campaignView.toast.messageUpdated.detail'),
        life: 2000
      });
    }
  }

  /**
   * Cancel editing a message
   */
  cancelEditMessage(): void {
    this.editingMessageIndex = -1;
    this.messageEditBuffer = [];
  }

  /**
   * Add a new question-answer pair to the default Q&A configuration
   */
  addDefaultQuestionAnswer(): void {
    if (!this.newQuestion.trim() || !this.newAnswer.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('supervisor.campaignView.toast.incompleteFields.summary'),
        detail: this.translate.instant('supervisor.campaignView.toast.incompleteFields.detailQA'),
        life: 3000
      });
      return;
    }

    const qaControl = this.campaignForm.get('default_questions_answers_config.questions_answers');
    const currentQA = qaControl?.value || [];

    qaControl?.setValue([...currentQA, {
      question: this.newQuestion.trim(),
      answer: this.newAnswer.trim()
    }]);
    this.campaignForm.markAsDirty();
    this.newQuestion = '';
    this.newAnswer = '';

    this.messageService.add({
      severity: 'success',
      summary: this.translate.instant('supervisor.campaignView.toast.qaAdded.summary'),
      detail: this.translate.instant('supervisor.campaignView.toast.qaAdded.detail'),
      life: 2000
    });
  }

  /**
   * Remove a question-answer pair from the default Q&A configuration
   */
  removeDefaultQuestionAnswer(index: number): void {
    const qaControl = this.campaignForm.get('default_questions_answers_config.questions_answers');
    const currentQA = qaControl?.value || [];

    if (index >= 0 && index < currentQA.length) {
      currentQA.splice(index, 1);
      qaControl?.setValue([...currentQA]);
      this.campaignForm.markAsDirty();

      this.messageService.add({
        severity: 'info',
        summary: this.translate.instant('supervisor.campaignView.toast.qaRemoved.summary'),
        detail: this.translate.instant('supervisor.campaignView.toast.qaRemoved.detail'),
        life: 2000
      });
    }
  }

  /**
   * Start editing a Q&A pair at the specified index
   */
  editDefaultQuestionAnswer(index: number): void {
    const qaControl = this.campaignForm.get('default_questions_answers_config.questions_answers');
    const currentQA = qaControl?.value || [];
    if (index >= 0 && index < currentQA.length) {
      this.editingQAIndex = index;
      this.qaEditBuffer[index] = {
        question: currentQA[index].question,
        answer: currentQA[index].answer
      };
    }
  }

  /**
   * Update an existing Q&A pair
   */
  updateDefaultQuestionAnswer(): void {
    const qaControl = this.campaignForm.get('default_questions_answers_config.questions_answers');
    const currentQA = qaControl?.value || [];
    if (this.editingQAIndex >= 0 && this.editingQAIndex < currentQA.length) {
      const buffer = this.qaEditBuffer[this.editingQAIndex];
      if (!buffer.question.trim() || !buffer.answer.trim()) {
        this.messageService.add({
          severity: 'warn',
          summary: this.translate.instant('supervisor.campaignView.toast.incompleteFields.summary'),
          detail: this.translate.instant('supervisor.campaignView.toast.incompleteFields.detailQA'),
          life: 3000
        });
        return;
      }
      currentQA[this.editingQAIndex] = {
        question: buffer.question.trim(),
        answer: buffer.answer.trim()
      };
      qaControl?.setValue([...currentQA]);
      this.campaignForm.markAsDirty();
      this.cancelEditQuestionAnswer();
      this.messageService.add({
        severity: 'success',
        summary: this.translate.instant('supervisor.campaignView.toast.qaUpdated.summary'),
        detail: this.translate.instant('supervisor.campaignView.toast.qaUpdated.detail'),
        life: 2000
      });
    }
  }

  /**
   * Cancel editing a Q&A pair
   */
  cancelEditQuestionAnswer(): void {
    this.editingQAIndex = -1;
    this.qaEditBuffer = [];
  }  /**
   * Validate that all required configuration sections are properly filled
   */
  validateConfigurationSections(): boolean {
    let isValid = true;
    const errors: string[] = [];

    // Check translation config
    const translationEnabled = this.campaignForm.get('translation_config.enable')?.value;
    if (translationEnabled) {
      const sourceLanguage = this.campaignForm.get('translation_config.source_language')?.value;
      const targetLanguage = this.campaignForm.get('translation_config.target_language')?.value;

      if (!sourceLanguage || !targetLanguage) {
        errors.push(this.translate.instant('supervisor.campaignView.validation.translationLanguages'));
        isValid = false;
      }
    }

    // Check conversation config
    const conversationEnabled = this.campaignForm.get('conversation_config.allow_automatic_message')?.value;
    if (conversationEnabled) {
      const howOften = this.campaignForm.get('conversation_config.how_often_message')?.value;
      const message = this.campaignForm.get('conversation_config.automatic_message_value')?.value;

      if (!howOften || howOften <= 0) {
        errors.push(this.translate.instant('supervisor.campaignView.validation.frequencyGreaterThanZero'));
        isValid = false;
      }

      if (!message || message.trim().length === 0) {
        errors.push(this.translate.instant('supervisor.campaignView.validation.autoMessageEmpty'));
        isValid = false;
      }
    }

    // Check time settings config
    const timeSettingsEnabled = this.campaignForm.get('time_settings_config.enable')?.value;
    if (timeSettingsEnabled) {
      const days = this.campaignForm.get('time_settings_config.days')?.value;

      if (!days || days.length === 0) {
        errors.push(this.translate.instant('supervisor.campaignView.validation.atLeastOneDay'));
        isValid = false;
      }
    }

    // Check default messages config
    const messagesEnabled = this.campaignForm.get('default_messages_config.enable')?.value;
    if (messagesEnabled) {
      const messages = this.campaignForm.get('default_messages_config.messages')?.value;

      if (!messages || messages.length === 0) {
        errors.push(this.translate.instant('supervisor.campaignView.validation.atLeastOneMessage'));
        isValid = false;
      }
    }

    // Check default Q&A config
    const qaEnabled = this.campaignForm.get('default_questions_answers_config.enable')?.value;
    if (qaEnabled) {
      const qa = this.campaignForm.get('default_questions_answers_config.questions_answers')?.value;

      if (!qa || qa.length === 0) {
        errors.push(this.translate.instant('supervisor.campaignView.validation.atLeastOneQA'));
        isValid = false;
      } else {
        // Validate that all Q&A pairs have both question and answer
        const invalidQA = qa.some((item: any) => !item.question?.trim() || !item.answer?.trim());
        if (invalidQA) {
          errors.push(this.translate.instant('supervisor.campaignView.validation.qaContentRequired'));
          isValid = false;
        }
      }
    }

    // Show errors if any
    if (errors.length > 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('supervisor.campaignView.toast.incompleteConfig.summary'),
        detail: errors.join('. '),
        life: 5000
      });
    }

    return isValid;
  }

  /**
   * Handle form submission - validates form and calls appropriate create/update method
   */
  onSubmit(): void {
    if (this.campaignForm.invalid) {
      this.markFormGroupTouched(this.campaignForm);
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('supervisor.campaignView.toast.incompleteForm.summary'),
        detail: this.translate.instant('supervisor.campaignView.toast.incompleteForm.detail'),
        life: 3000
      });
      return;
    }

    // Validate configuration sections
    if (!this.validateConfigurationSections()) {
      return;
    }

    if (this.campaignId) {
      this.updateCampaign();
    } else {
      // this.createCampaign();
      this.testCreateCampaign();
    }
  }

  // Form control getters for easy template access and better performance
  get nameControl() { return this.campaignForm.get('name'); }
  get campaignNameControl() { return this.campaignForm.get('campaign_name'); }
  get typeControl() { return this.campaignForm.get('type'); }
  get stateControl() { return this.campaignForm.get('state'); }
  get priorityControl() { return this.campaignForm.get('priority'); }
  get ratioControl() { return this.campaignForm.get('ratio'); }
  get currentActionControl() { return this.campaignForm.get('current_action'); }
  get stateSinceControl() { return this.campaignForm.get('state_since'); }
  get closingReasonControl() { return this.campaignForm.get('closing_reason'); }

  // Configuration section getters
  get generalConfigControl() { return this.campaignForm.get('general_config'); }
  get translationConfigControl() { return this.campaignForm.get('translation_config'); }
  get conversationConfigControl() { return this.campaignForm.get('conversation_config'); }
  get timeSettingsConfigControl() { return this.campaignForm.get('time_settings_config'); }
  get agentConfigControl() { return this.campaignForm.get('agent_config'); }
  get defaultMessagesConfigControl() { return this.campaignForm.get('default_messages_config'); }
  get defaultQuestionsAnswersConfigControl() { return this.campaignForm.get('default_questions_answers_config'); }

  /**
   * Check if a configuration section is enabled
   */
  isConfigSectionEnabled(sectionName: string): boolean {
    return this.campaignForm.get(`${sectionName}.enable`)?.value || false;
  }

  /**
   * Get configuration section enable control
   */
  getConfigSectionEnableControl(sectionName: string) {
    return this.campaignForm.get(`${sectionName}.enable`);
  }

  /**
   * Clear all default messages with confirmation
   */
  clearAllDefaultMessages(): void {
    this.confirmationService.confirm({
      message: this.translate.instant('supervisor.campaignView.confirm.clearMessages.message'),
      header: this.translate.instant('supervisor.campaignView.confirm.header'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('supervisor.campaignView.confirm.acceptAllMale'),
      rejectLabel: this.translate.instant('supervisor.campaignView.confirm.reject'),
      accept: () => {
        const messagesControl = this.campaignForm.get('default_messages_config.messages');
        messagesControl?.setValue([]);
        this.campaignForm.markAsDirty();

        this.messageService.add({
          severity: 'info',
          summary: this.translate.instant('supervisor.campaignView.toast.messagesCleared.summary'),
          detail: this.translate.instant('supervisor.campaignView.toast.messagesCleared.detail'),
          life: 3000
        });
      }
    });
  }

  /**
   * Clear all default Q&A pairs with confirmation
   */
  clearAllDefaultQuestionAnswers(): void {
    this.confirmationService.confirm({
      message: this.translate.instant('supervisor.campaignView.confirm.clearQA.message'),
      header: this.translate.instant('supervisor.campaignView.confirm.header'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('supervisor.campaignView.confirm.acceptAllFemale'),
      rejectLabel: this.translate.instant('supervisor.campaignView.confirm.reject'),
      accept: () => {
        const qaControl = this.campaignForm.get('default_questions_answers_config.questions_answers');
        qaControl?.setValue([]);
        this.campaignForm.markAsDirty();

        this.messageService.add({
          severity: 'info',
          summary: this.translate.instant('supervisor.campaignView.toast.qaCleared.summary'),
          detail: this.translate.instant('supervisor.campaignView.toast.qaCleared.detail'),
          life: 3000
        });
      }
    });
  }

  /**
   * Get character count class for styling
   */
  getCharacterCountClass(text: string, maxLength: number): string {
    const length = text?.length || 0;
    const percentage = (length / maxLength) * 100;

    if (percentage >= 100) {
      return 'character-limit-reached';
    } else if (percentage >= 85) {
      return 'character-limit-near';
    }
    return '';
  }

  get automaticNotificationsQueue(): FormArray {
    return this.campaignForm.get('conversation_config.automatic_notifications_queue') as FormArray;
  }

  /**
   * Add a new notification to the queue
   */
  addNotificationToQueue() {
    if (!this.newNotificationMessage || this.newNotificationTime == null) return;
    this.automaticNotificationsQueue.push(this.fb.group({
      id_retrie: [this.automaticNotificationsQueue.length + 1], // index+1
      message: [this.newNotificationMessage],
      time: [this.newNotificationTime]
    }));
    this.campaignForm.markAsDirty();
    this.newNotificationMessage = '';
    this.newNotificationTime = null;
  }

  /**
   * Remove a notification from the queue
   */
  removeNotificationFromQueue(i: number) {
    this.automaticNotificationsQueue.removeAt(i);
    // Re-index id_retrie
    this.automaticNotificationsQueue.controls.forEach((ctrl, idx) => ctrl.get('id_retrie')?.setValue(idx + 1));
    this.campaignForm.markAsDirty();
  }

  /**
   * Start editing a notification at the specified index
   */
  editNotification(index: number): void {
    this.editingNotificationIndex = index;
    const notif = this.automaticNotificationsQueue.at(index).value;
    this.notificationEditBuffer[index] = {
      message: notif.message,
      time: notif.time
    };
  }

  /**
   * Update an existing notification
   */
  updateNotification(): void {
    if (this.editingNotificationIndex < 0) return;
    const buffer = this.notificationEditBuffer[this.editingNotificationIndex];
    if (!buffer.message?.trim() || buffer.time == null) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('supervisor.campaignView.toast.incompleteFields.summary'),
        detail: this.translate.instant('supervisor.campaignView.toast.incompleteFields.detailNotification'),
        life: 3000
      });
      return;
    }
    this.automaticNotificationsQueue.at(this.editingNotificationIndex).patchValue({
      message: buffer.message.trim(),
      time: buffer.time
    });
    this.campaignForm.markAsDirty();
    this.cancelEditNotification();
    this.messageService.add({
      severity: 'success',
      summary: this.translate.instant('supervisor.campaignView.toast.notificationUpdated.summary'),
      detail: this.translate.instant('supervisor.campaignView.toast.notificationUpdated.detail'),
      life: 2000
    });
  }

  /**
   * Cancel editing a notification
   */
  cancelEditNotification(): void {
    this.editingNotificationIndex = -1;
    this.notificationEditBuffer = [];
  }

  /**
   * Clear all notifications with confirmation
   */
  clearAllNotifications(): void {
    this.confirmationService.confirm({
      message: this.translate.instant('supervisor.campaignView.confirm.clearNotifications.message'),
      header: this.translate.instant('supervisor.campaignView.confirm.header'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('supervisor.campaignView.confirm.acceptAllFemale'),
      rejectLabel: this.translate.instant('supervisor.campaignView.confirm.reject'),
      accept: () => {
        while (this.automaticNotificationsQueue.length > 0) {
          this.automaticNotificationsQueue.removeAt(0);
        }
        this.campaignForm.markAsDirty();
        this.messageService.add({
          severity: 'info',
          summary: this.translate.instant('supervisor.campaignView.toast.notificationsCleared.summary'),
          detail: this.translate.instant('supervisor.campaignView.toast.notificationsCleared.detail'),
          life: 3000
        });
      }
    });
  }

}
